import { useMemo, useState } from 'react';

import { recallApiKey, resolveApiBase, resolveSdkBase } from '../api.js';
import { ChevronDownIcon, ChevronUpIcon } from './icons';

/**
 * Shared "drag me to the bookmark bar" widget (card 063).
 *
 * Two pages render this now:
 *
 *   - `/projects/:slug/bookmarklet`  → ProjectBookmarklet (one project)
 *   - `/my-keys#bookmarklet`         → MyKeys (every personal pk key)
 *
 * Lifted verbatim from MyKeys' BookmarkletKeyRow so a future iteration
 * (different SDK options, an "auto-update on revoke" flag, etc.) only
 * touches one place.  Hidden behavior worth keeping in mind:
 *
 *   - Plaintext lives only in sessionStorage on the browser that
 *     minted the key — `recallApiKey` returns null on every other
 *     browser / session.  When we have it we generate a fully-formed
 *     bookmarklet href; when we don't, the widget says "this key was
 *     minted elsewhere, mint a fresh one here to drag".
 *   - React 19 blocks `javascript:` URLs going through JSX props, so
 *     we set the href via a ref after mount.  This is the one place
 *     the dashboard legitimately needs a `javascript:` URL.
 */
export function BookmarkletDragWidget({
  apiKey,         // { prefix, name } row metadata — required.
  projectSlug = '',
  projectName = '',
  label,          // override the default "Spotlight admin · <name>"
  defaultExpanded = false,
}) {
  const apiBase = resolveApiBase();
  const sdkBase = useMemo(() => {
    return resolveSdkBase()
      || (typeof window !== 'undefined' ? window.location.origin : '');
  }, []);

  const [expanded, setExpanded] = useState(defaultExpanded);

  const plaintext = recallApiKey(apiKey.prefix);
  const href = useMemo(() => {
    if (!plaintext) return null;
    return buildBookmarkletHref({
      apiKey: plaintext,
      sdkBase,
      apiBase,
      projectSlug,
    });
  }, [plaintext, apiBase, sdkBase, projectSlug]);

  const linkLabel = label
    || `Spotlight admin${projectName ? ` · ${projectName}` : ''} · ${apiKey.name || apiKey.prefix}`;

  return (
    <div className="bookmarklet-widget">
      <style>{BOOKMARKLET_WIDGET_CSS}</style>
      {href ? (
        <button
          type="button"
          className="btn btn--ghost bookmarklet-widget__toggle"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
        >
          {expanded ? <ChevronUpIcon /> : <ChevronDownIcon />}
          {expanded ? 'Hide drag link' : 'Show drag link'}
        </button>
      ) : (
        <div className="state" style={{ marginTop: 8 }}>
          <div className="state__title">No plaintext on this browser</div>
          <div>
            We cache plaintext only in the session it was minted in,
            so we can&apos;t generate a bookmarklet URL from this row.
            Mint a fresh bookmarklet key here and drag it immediately.
          </div>
        </div>
      )}
      {expanded && href && (
        <div className="bookmarklet-widget__drag">
          <p className="bookmarklet-widget__hint">
            Drag this link onto your bookmark bar.  Typing{' '}
            <code>javascript:</code> URLs into the address bar is
            blocked by every modern browser, so drag is the only
            reliable install.
          </p>
          <BookmarkletLink href={href} label={linkLabel} />
          <details className="bookmarklet-widget__raw-toggle">
            <summary>Inspect the raw bookmarklet</summary>
            <pre className="bookmarklet-widget__raw">{href}</pre>
          </details>
        </div>
      )}
    </div>
  );
}

/**
 * Render an `<a>` tag with a `javascript:` href.
 *
 * React 19 refuses to set `javascript:` URLs through JSX props
 * ("React has blocked a javascript: URL as a security precaution")
 * because 99% of the time they indicate an XSS mistake.  Dragging
 * a link into the bookmark bar is the one place they're legitimate,
 * so we bypass the guard by assigning `href` through a ref after
 * mount — React never sees the attribute going through JSX, and
 * the browser accepts the drop normally.
 */
function BookmarkletLink({ href, label }) {
  const setRef = (el) => {
    if (el && href) el.setAttribute('href', href);
  };
  return (
    <a ref={setRef} className="bookmarklet-widget__link">
      {label}
    </a>
  );
}

/**
 * Build the `javascript:…` URL that the bookmarklet drops onto the
 * bookmark bar.  Reload semantics: always evict any existing
 * `window.Spotlight` and matching <script> tags before injecting a
 * fresh SDK bundle, so clicking the bookmarklet on a page that
 * already has Spotlight embedded (demo, customer prod page) reuses
 * the new bundle rather than the in-page one.
 *
 * Kept on one long line in the output so Firefox doesn't choke on
 * bookmarklets with literal newlines.
 */
export function buildBookmarkletHref({ apiKey, sdkBase, apiBase, projectSlug }) {
  const payload = [
    '(function(){',
    `var base=${JSON.stringify((apiBase || '').replace(/\/+$/, ''))};`,
    `var sdkBase=${JSON.stringify((sdkBase || '').replace(/\/+$/, ''))};`,
    `var opts={apiKey:${JSON.stringify(apiKey)},baseUrl:base,autoStart:true,demoMode:"admin"`,
    projectSlug ? `,project:${JSON.stringify(projectSlug)}` : '',
    '};',
    'try{window.Spotlight&&window.Spotlight.destroy&&window.Spotlight.destroy();}catch(e){}',
    'try{delete window.Spotlight;}catch(e){window.Spotlight=undefined;}',
    'try{delete window.__SpotlightAdmin;}catch(e){window.__SpotlightAdmin=undefined;}',
    'document.querySelectorAll(\'script[src*="spotlight.min.js"],script[src*="admin.min.js"]\').forEach(function(n){n.parentNode&&n.parentNode.removeChild(n);});',
    'function boot(){if(!window.Spotlight){return setTimeout(boot,50);}try{window.Spotlight.destroy&&window.Spotlight.destroy();}catch(e){}window.Spotlight.init(opts);}',
    'var s=document.createElement("script");',
    's.src=sdkBase+"/latest/spotlight.min.js?v="+Date.now();',
    's.async=true;s.onload=boot;',
    's.onerror=function(){alert("Spotlight: failed to load "+s.src);};',
    'document.head.appendChild(s);',
    '})();',
  ].join('');
  return 'javascript:' + encodeURIComponent(payload);
}

const BOOKMARKLET_WIDGET_CSS = `
  .bookmarklet-widget {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
  .bookmarklet-widget__toggle {
    align-self: flex-start;
  }
  .bookmarklet-widget__drag {
    padding: 12px;
    background: var(--surface-1);
    border-radius: 10px;
  }
  .bookmarklet-widget__hint {
    font-size: 12.5px;
    color: var(--text-3);
    margin: 0 0 8px;
  }
  .bookmarklet-widget__link {
    display: inline-block;
    padding: 12px 22px;
    font-size: 15px;
    font-weight: 700;
    background: var(--brand-600);
    color: var(--text-inverse);
    border-radius: var(--r-md);
    text-decoration: none;
    box-shadow: var(--shadow-2);
    cursor: grab;
  }
  .bookmarklet-widget__link:hover {
    background: var(--brand-700);
    text-decoration: none;
  }
  .bookmarklet-widget__raw-toggle {
    margin-top: 12px;
  }
  .bookmarklet-widget__raw-toggle summary {
    cursor: pointer;
    color: var(--text-3);
    font-size: 12.5px;
  }
  .bookmarklet-widget__raw {
    background: var(--text-1);
    color: var(--surface-muted);
    padding: 12px;
    border-radius: var(--r-md, 10px);
    white-space: pre-wrap;
    word-break: break-all;
    font-size: 11.5px;
    margin: 10px 0 0;
    max-height: 200px;
    overflow: auto;
  }
`;
